import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../actions/api";
import { hideLoader, showLoader } from "./loaderSlice";

export const getKennelList = createAsyncThunk(
  "getKennelList",
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(
        `/getall/clinic/facilities`
      );

      if (res.status === 200) {
        const kennels = res.data.data.filter((p) => p.type === "Kennel");
        thunkAPI.dispatch(hideLoader());
        return kennels;
      }
      thunkAPI.dispatch(hideLoader());
      return [];
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch kennels");
    }
  }
);

export const createKennel = createAsyncThunk(
  "createKennel",
  async ({ name, status = true }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/create/clinic/facilites?type=Kennel&name=${name}&status=${status}`
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to create kennel");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const updateKennel = createAsyncThunk(
  "updateKennel",
  async ({ facilityId, name, status = true }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).put(
        `/update/clinic/facilites/${facilityId}?type=Kennel&name=${name}&status=${status}`
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to update kennel");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const deleteKennel = createAsyncThunk(
  "deleteKennel",
  async ({ facilityId }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).delete(
        `/delete/clinic/facilites/${facilityId}`
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return facilityId; // Return ID to remove from list
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to delete kennel");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

const kennelSlice = createSlice({
  name: "kennel",
  initialState: {
    list: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getKennelList.fulfilled, (state, action) => {
        state.list = action.payload ?? [];
        state.loading = false;
        state.error = null;
      })
      .addCase(createKennel.fulfilled, (state, action) => {
        state.list.push(action.payload);
        state.loading = false;
        state.error = null;
      })
      .addCase(updateKennel.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.list.findIndex(
          (p) => p.facilityId === updated.facilityId
        );
        if (index !== -1) {
          state.list[index] = updated;
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteKennel.fulfilled, (state, action) => {
        const id = action.payload;
        state.list = state.list.filter((p) => p.facilityId !== id);
        state.loading = false;
        state.error = null;
      })
      .addCase(getKennelList.pending, (state) => {
        state.loading = true;
      })
      .addCase(createKennel.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateKennel.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteKennel.pending, (state) => {
        state.loading = true;
      })
      .addCase(getKennelList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error fetching kennels.";
      });
  },
});

export default kennelSlice.reducer;
